import { useMemo } from 'react';
import { isArray } from 'lodash-es';
import { useGetGlobalFilters } from '@/utils/cube';
import { useGetUserCountries } from '@/api/queries/country/useGetUserCountries';
import { isStringArray } from '@/api/queries/utils';
import { extractFilterFromGlobalFilters } from '@/api/hcp/partnerscore/utils';
import { useFiltersContext } from '@/context/FiltersContext/FiltersContextProvider';
import { FILTER_CONFIG, FilterType } from '@/models/filter';
import { isActiveFilter } from '@/types/filter';

const usePartnerScoreGlobalFilter = () => {
  const { filterState } = useFiltersContext();
  const globalFilters = useGetGlobalFilters();
  const countryCodesFromGlobalFilters = useMemo(() => {
    const value = filterState[FilterType.COUNTRY];
    const config = FILTER_CONFIG[FilterType.COUNTRY];

    if (!value || !isArray(value) || value.length === 0 || !isActiveFilter(config)) return [];

    const filterExpression = config.applyFilter(value);
    if (!filterExpression || !('values' in filterExpression) || !isStringArray(filterExpression.values)) return [];

    return filterExpression.values;
  }, [filterState]);

  const { data: allCountries = [], isLoading: isAllCountriesLoading } = useGetUserCountries();

  const countries: string[] = useMemo(() => {
    if (isAllCountriesLoading) return [];
    if (countryCodesFromGlobalFilters.length === 0) return allCountries?.map(country => country.value);

    return countryCodesFromGlobalFilters;
  }, [countryCodesFromGlobalFilters, allCountries, isAllCountriesLoading]);

  const filtersWithoutHcpCountryCode = useMemo(
    () =>
      extractFilterFromGlobalFilters({
        globalFilters,
        filterToExclude: 'hcp.country_code',
      }),
    [globalFilters],
  );

  const globalFiltersWithoutHcpCountryCode = useMemo(
    () => ({
      ...globalFilters,
      and: [...filtersWithoutHcpCountryCode],
    }),
    [globalFilters, filtersWithoutHcpCountryCode],
  );

  return {
    globalFilters,
    countries,
    globalFiltersWithoutHcpCountryCode,
  };
};

export default usePartnerScoreGlobalFilter;
